import React, { useEffect, useState, useCallback } from "react";
import { fetchAuthSession } from "@aws-amplify/auth";
import { QRCodeCanvas } from "qrcode.react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import ElectrikCarousel from "./ElectrikCarousel";

const API_BASE_URL = "https://api.cupvisor.org/v2/vendor_portal";
const cardWidth = "260px";
const cardHeight = "220px";
const BASIC_COLORS = [
  { label: "— Select color —", value: "" },
  { label: "Red", value: "#FF0000" },
  { label: "Orange", value: "#FF8C00" },
  { label: "Yellow", value: "#FFD700" },
  { label: "Green", value: "#28A745" },
  { label: "Teal", value: "#20C997" },
  { label: "Blue", value: "#007BFF" },
  { label: "Indigo", value: "#6610F2" },
  { label: "Purple", value: "#6F42C1" },
  { label: "Pink", value: "#E83E8C" },
  { label: "Black", value: "#000000" },
  { label: "White", value: "#FFFFFF" }
];

function VendorPortalPage({ user, signOut }) {
  const [jwtToken, setJwtToken] = useState("");
  const [vendors, setVendors] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // QR Modal state
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedQRCode, setSelectedQRCode] = useState("");

  // Creation modal state
  const [createModalShow, setCreateModalShow] = useState(false);
  const [createStep, setCreateStep] = useState("form"); // "form" or "preview"
  const [currentVendorId, setCurrentVendorId] = useState(null);
  const [newCard, setNewCard] = useState({
    description: "",
    points_required: "",
    reward: "",
    point_source: "",
    colore: "" 
  });

  useEffect(() => {
    async function loadSession() {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        setJwtToken(idToken);
      } catch (err) {
        console.error("Error loading session:", err);
      }
    }
    loadSession();
  }, [user]);

  const fetchVendorCards = useCallback(async () => {
    if (!jwtToken) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
      });
      if (res.status === 200) {
        const data = await res.json();
        setVendors(data.vendors);
        setError("");
      } else if (res.status === 403) {
        setError("Not authorized to view card types.");
      } else {
        setError("Error fetching card types.");
      }
    } catch (err) {
      console.error("Error fetching card types:", err);
      setError("Error fetching card types.");
    }
    setLoading(false);
  }, [jwtToken]);

  useEffect(() => {
    if (jwtToken) {
      fetchVendorCards();
    }
  }, [jwtToken, fetchVendorCards]);

  const handleShowQRCode = (qrValue) => {
    setSelectedQRCode(qrValue);
    setShowQRModal(true);
  };

  const handleDeleteCard = async (vendorId, cardId) => {
    if (!window.confirm("Are you sure you want to delete this card type?")) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}?vendorId=${vendorId}&cardTypeId=${cardId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          "Accept": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
        }
      );
      if (res.status === 200) {
        fetchVendorCards();
      } else {
        setError("Error deleting card type.");
      }
    } catch (err) {
      console.error("Error deleting card type:", err);
      setError("Error deleting card type.");
    }
  };

  const handleConfirmCreateCardType = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}?vendorId=${currentVendorId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
        body: JSON.stringify(newCard),
      });
      if (res.status === 201) {
        setNewCard({
          description: "",
          points_required: "",
          reward: "",
          point_source: "",
          colore: ""       
        });
        setCreateStep("form");
        setCreateModalShow(false);
        fetchVendorCards();
      } else {
        setError("Error creating card type.");
      }
    } catch (err) {
      console.error("Error creating card type:", err);
      setError("Error creating card type.");
    }
  };

  // Common card styling for slides
  const cardStyle = {
    border: "1px solid #ff66cc",
    borderRadius: "8px",
    overflow: "hidden",
    width: cardWidth,
    height: cardHeight,
    background: "linear-gradient(135deg, #ff66cc, #9933ff)",
    padding: "0.5rem",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    boxShadow: "none",
  };

  // Plus-card styling for adding a new card type
  const plusCardStyle = {
    border: "2px dashed #ff66cc",
    borderRadius: "8px",
    width: cardWidth,
    height: cardHeight,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    background: "linear-gradient(135deg, #ff66cc, #9933ff)",
  };

  // Render a single vendor card slide
  const renderVendorCardSlide = (card, vendor) => {
    if (card.isAdd) {
      return (
        <div
          key="add-card"
          style={plusCardStyle}
          onClick={() => {
            setCurrentVendorId(vendor.vendor_id);
            setNewCard({
              description: "",
              points_required: "",
              reward: "",
              point_source: "",
              colore: ""       
            });
            setCreateStep("form");
            setCreateModalShow(true);
          }}
        >
          <i className="bi bi-plus-circle" style={{ fontSize: "3rem", color: "#ffffff" }}></i>
        </div>
      );
    }
    return (
      <div key={card.card_type_id} style={cardStyle}>
        <div>
          <h5 style={{ fontWeight: "600", fontSize: "1rem", color: "#ffffff", marginBottom: "0.25rem" }}>
            {card.description}
          </h5>
          <p style={{ color: "#f0e6f7", margin: 0 }}>
            {vendor.name} <br />
          </p>
          <p style={{ color: "#ffffff", margin: "0.5rem 0 0" }}>
            <strong>Points:</strong> {card.points_required}
          </p>
          <p style={{ color: "#ffffff", margin: "0.5rem 0 0" }}>
            <strong>Reward:</strong> {card.reward}
          </p>
          <p style={{ color: "#ffffff", margin: "0.5rem 0 0" }}>
            <strong>Source:</strong> {card.point_source}
          </p>
        </div>
        <div className="d-flex justify-content-between align-items-center mt-2">
          {(vendor.role === "owner" ||
            vendor.role === "admin" ||
            vendor.role === "manager") && (
            <button
              onClick={() => handleDeleteCard(vendor.vendor_id, card.card_type_id)}
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "#ffcc00",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
              title="Delete Card"
            >
              <i className="bi bi-trash" style={{ fontSize: "1rem", color: "#ffffff" }}></i>
            </button>
          )}
          <div style={{ cursor: "pointer" }} onClick={() => handleShowQRCode(card.join_url)}>
            <div style={{ position: "relative", width: "48px", height: "48px" }}>
              <QRCodeCanvas value={card.join_url} size={48} bgColor="#9933ff" />
              <i
                className="bi bi-arrow-up-right-square"
                style={{
                  position: "absolute",
                  bottom: "2px",
                  right: "2px",
                  fontSize: "0.9rem",
                  color: "#ffffff",
                }}
              ></i>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container my-4">
      <h2 className="text-center">Vendor Portal</h2>
      {loading && <p className="text-center">Loading card types...</p>}
      {error && <div className="alert alert-danger text-center">{error}</div>}
      {!loading && !error && vendors.length > 0 ? (
        vendors.map((vendor) => (
          <div key={vendor.vendor_id} className="mb-5 text-center">
            <h3 style={{ margin: 0, fontWeight: 600, fontSize: "1.8rem", letterSpacing: "0.4px" }}>
                {vendor.name}
            </h3>
            <p style={{ margin: 0, fontSize: "1.2rem", opacity: 0.9 }}>
                {vendor.role}
                </p>

            {vendor.cards && vendor.cards.length > 0 ? (
              <ElectrikCarousel
                items={[...vendor.cards, { isAdd: true }]}
                renderSlide={(card, index) => renderVendorCardSlide(card, vendor)}
                containerStyle={{
                  width: "100%",
                  height: cardHeight,
                  margin: "0 auto",
                }}
              />
            ) : (
              <div className="d-flex flex-column align-items-center">
                <p>Add your first card!</p>
                <div
                  style={plusCardStyle}
                  onClick={() => {
                    setCurrentVendorId(vendor.vendor_id);
                    setNewCard({
                      description: "",
                      points_required: "",
                      reward: "",
                      point_source: "",
                    });
                    setCreateStep("form");
                    setCreateModalShow(true);
                  }}
                >
                  <i className="bi bi-plus-circle" style={{ fontSize: "3rem", color: "#ffffff" }}></i>
                </div>
              </div>
            )}
          </div>
        ))
      ) : (
        !loading && <p className="text-center">No vendors available for your account.</p>
      )}

      {/* QR Code Modal */}
      <Modal show={showQRModal} onHide={() => setShowQRModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: "none" }} />
        <Modal.Body
          className="text-center"
          style={{ backgroundColor: "#9933ff", padding: "2rem" }}
        >
          <QRCodeCanvas
            value={selectedQRCode}
            size={300}
            bgColor="#9933ff"
            fgColor="#ffffff"
            level="H"
            style={{ margin: "auto" }}
          />
        </Modal.Body>
      </Modal>

      {/* Create Card Modal */}
      <Modal show={createModalShow} onHide={() => setCreateModalShow(false)} centered>
        <Modal.Header 
          closeButton={createStep === "form"} 
          style={{ 
            backgroundColor: "#9933ff", 
            borderBottom: "none",
            display: createStep === "preview" ? "none" : "block"
          }}
        >
          <Modal.Title style={{ color: "#ffffff" }}>
            {createStep === "form" ? "Create New Card Type" : "Card Preview"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body 
        style={{
            backgroundColor: "#9933ff",
            color: "#ffffff",
            padding: "2rem",
            minHeight: "500px",   // ensures at least 500px of height
            maxHeight: "90vh",    // optional: 90% of the viewport
            overflowY: "auto",    // makes content scroll if it exceeds maxHeight
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between"
        }}>
          {createStep === "form" ? (
            <Form>
              <Form.Group controlId="cardDescription">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter description"
                  value={newCard.description}
                  onChange={(e) =>
                    setNewCard({ ...newCard, description: e.target.value })
                  }
                  required
                />
              </Form.Group>
              <Form.Group controlId="pointsRequired" className="mt-3">
                <Form.Label>Points Required</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter points required"
                  value={newCard.points_required}
                  onChange={(e) =>
                    setNewCard({ ...newCard, points_required: e.target.value })
                  }
                  required
                />
              </Form.Group>
              <Form.Group controlId="reward" className="mt-3">
                <Form.Label>Reward</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter reward details"
                  value={newCard.reward}
                  onChange={(e) =>
                    setNewCard({ ...newCard, reward: e.target.value })
                  }
                  required
                />
              </Form.Group>
              <Form.Group controlId="pointSource" className="mt-3">
                <Form.Label>Point Source</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter point source"
                  value={newCard.point_source}
                  onChange={(e) =>
                    setNewCard({ ...newCard, point_source: e.target.value })
                  }
                  required
                />
              </Form.Group>
            <Form.Group controlId="cardColor" className="mt-3">
            <Form.Label>Card Color</Form.Label>
            <Form.Select
              value={newCard.colore}
              onChange={(e) =>
                setNewCard({ ...newCard, colore: e.target.value })
              }
            >
              {BASIC_COLORS.map((c) => (
                <option key={c.value || "none"} value={c.value}>
                  {c.label} {c.value ? `(${c.value})` : ""}
                </option>
              ))}
            </Form.Select>
            {/* tiny swatch preview */}
            {newCard.colore ? (
              <div
                style={{
                  marginTop: "8px",
                  width: "100%",
                  height: "28px",
                  borderRadius: "6px",
                  border: "1px solid rgba(255,255,255,0.35)",
                  background: newCard.colore
                }}
                aria-label="Selected color preview"
              />
            ) : null}
          </Form.Group>
            </Form>
          ) : (
            <div style={{ 
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center"
            }}>
              <h3 className="text-center mb-4">Card Preview</h3>
              <div style={{ 
                ...cardStyle,
                margin: "0 auto",
                transform: "scale(1.1)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.2)"
              }}>
                <div>
                  <h5 style={{ 
                    fontWeight: "600", 
                    fontSize: "1rem", 
                    color: "#ffffff", 
                    marginBottom: "0.25rem" 
                  }}>
                    {newCard.description}
                  </h5>
                  <p style={{ color: "#f0e6f7", margin: 0 }}>
                    {vendors.find(v => v.vendor_id === currentVendorId)?.name || "Vendor Name"}
                    <br />
                  </p>
                  <p style={{ color: "#ffffff", margin: "0.5rem 0 0" }}>
                    <strong>Points:</strong> {newCard.points_required}
                  </p>
                  <p style={{ color: "#ffffff", margin: "0.5rem 0 0" }}>
                    <strong>Reward:</strong> {newCard.reward}
                  </p>
                  <p style={{ color: "#ffffff", margin: "0.5rem 0 0" }}>
                    <strong>Source:</strong> {newCard.point_source}
                  </p>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <button
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "8px",
                      border: "none",
                      backgroundColor: "#ffcc00",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "not-allowed",
                    }}
                    disabled
                  >
                    <i className="bi bi-trash" style={{ fontSize: "1rem", color: "#ffffff" }}></i>
                  </button>
                  <div style={{ position: "relative", width: "48px", height: "48px" }}>
                    <QRCodeCanvas value="preview" size={48} bgColor="#9933ff" />
                    <i
                      className="bi bi-arrow-up-right-square"
                      style={{
                        position: "absolute",
                        bottom: "2px",
                        right: "2px",
                        fontSize: "0.9rem",
                        color: "#ffffff",
                      }}
                    ></i>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="d-flex justify-content-between mt-4">
            <Button
              variant="outline-danger"
              onClick={() => {
                if (createStep === "form") {
                  setCreateModalShow(false);
                } else {
                  setCreateStep("form");
                }
              }}
              style={{ border: "none", backgroundColor: "transparent" }}
            >
              <i className="bi bi-x-circle" style={{ fontSize: "2rem", color: "#ffffff" }}></i>
            </Button>
            <Button
              variant="outline-success"
              onClick={() => {
                if (createStep === "form") {
                  if (
                    newCard.description &&
                    newCard.points_required &&
                    newCard.reward &&
                    newCard.point_source
                  ) {
                    setCreateStep("preview");
                  }
                } else {
                  handleConfirmCreateCardType();
                }
              }}
              style={{ border: "none", backgroundColor: "transparent" }}
            >
              <i className="bi bi-check-circle" style={{ fontSize: "2rem", color: "#ffffff" }}></i>
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default VendorPortalPage;
